import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import EntityDetailDrawer from '../../EntityDetailDrawer';
import { Code, Section } from '../../Ontology/DetailPrimitives';
import { PanelError } from '../BenchmarkPrimitives';
import { humanise, scenarioName } from '../BenchmarkVocabulary';
import {
    BenchmarkRequestError, CaseComparison, CaseDetailResponse, ComparisonDimension, DimensionManifest, RunOutcome, benchmarkService
} from '../../../services/benchmarkService';
import { WithheldIndex } from '../withheldMetrics';
import { resultOutcomeLabel } from '../supplied/results/resultsVocabulary';
import { ComparisonAgreementBadge } from './ComparisonSubjectsTable';
import { DimensionBadges, DimensionDetailBody } from './DimensionDetail';
import { RunScenarios, alertTypologies } from './runScenarios';
import {
    DIMENSION_TYPE_LABELS, RouteStep, SIDE_LABELS, classLabel, comparisonStatusMeta, dimensionSentence, isPositiveClass,
    platformReason, producerLabel, routeStepName
} from './investigationVocabulary';
import { dsVar } from '../../../theme/tokens';

interface InvestigationDrawerProps {
    runKey: number | null;
    /** The compared item a scorecard row opened, or null when only a case is open. */
    dimension: ComparisonDimension | null;
    /** The case open over the item, or on its own from a Decisions card. */
    caseRef: string | null;
    /** The run's dimensions by key, for their labels and classes. */
    manifest: Map<string, ComparisonDimension>;
    /** The run's detection scenarios and their typologies; null on older runs. */
    scenarios?: RunScenarios;
    withheld: WithheldIndex;
    outcomes?: RunOutcome[];
    /** A case from the item's cases, opened in place of the item. */
    onCaseSelect: (caseRef: string) => void;
    /** Closes the case and returns to the item it was opened from. */
    onBack: () => void;
    onClose: () => void;
    expanded: boolean;
    onToggleExpand: () => void;
}

/** A comparison's dimension, from the manifest, or a stand-in named from its key. */
const dimensionFor = (manifest: Map<string, ComparisonDimension>, c: CaseComparison): DimensionManifest =>
    manifest.get(c.dimensionKey) ?? {
        dimensionKey: c.dimensionKey, dimensionType: c.dimensionType, label: humanise(c.dimensionKey.split(':').pop() || c.dimensionKey),
        valueKind: 'binary', classes: [], producer: c.producer, typologies: [], factors: [], flagAt: null, eddTrigger: null
    };

const Side: React.FC<{ dimension: DimensionManifest; value: string | null; status?: string | null }> = ({ dimension, value, status }) => (
    value === null
        ? <Box component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>{status ? comparisonStatusMeta(status).label : 'Not recorded'}</Box>
        : <Box component="span" sx={{ fontSize: '0.8125rem', fontWeight: 700, color: isPositiveClass(dimension, value) ? dsVar('accent-fg') : dsVar('fg-soft') }}>{classLabel(value, dimension)}</Box>
);

/** One dimension's two sides for this case, and why the agent replay's is what it is. */
const ComparisonRow: React.FC<{ comparison: CaseComparison; dimension: DimensionManifest; indent?: boolean }> = ({ comparison, dimension, indent }) => {
    const reason = platformReason(comparison.detail, comparison.platformScore, dimension);
    return (
        <Box sx={{ py: 1, pl: indent ? 2 : 0, borderBottom: `1px solid ${dsVar('line')}` }}>
            <Box className="flex items-center justify-between gap-3">
                <Typography sx={{ fontSize: indent ? '0.8125rem' : '0.875rem', fontWeight: indent ? 500 : 600, color: dsVar('fg-body') }}>
                    {dimension.label}
                </Typography>
                <ComparisonAgreementBadge kind={comparison.agreement} dimension={dimension} />
            </Box>
            <Typography component="div" sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.5 }}>
                {SIDE_LABELS.institution}: <Side dimension={dimension} value={comparison.institutionValue} />
                {' · '}{SIDE_LABELS.platform}: <Side dimension={dimension} value={comparison.platformValue} status={comparison.status} />
            </Typography>
            {reason && <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 0.25 }}>{reason}</Typography>}
        </Box>
    );
};

/** What a case's alerts claimed: typologies where the run had a mapping, else the rule. */
const scenarioIdsOf = (detail: CaseDetailResponse | null): string[] =>
    [...new Set((detail?.alerts ?? []).map((a) => a.scenarioId).filter((id): id is string => Boolean(id)))];

/** A case's outcome and what its alerts claimed, in a sentence, for the drawer's description. */
const caseDescription = (detail: CaseDetailResponse, scenarios: RunScenarios): string => {
    const scenarioIds = scenarioIdsOf(detail);
    const claimed = alertTypologies(scenarioIds, scenarios);
    const claimedLine = !scenarioIds.length ? ''
        : scenarios ? ` · alert typology: ${claimed.typologies.map((t) => t.label).join(', ') || 'not mapped'}`
            : ` · ${scenarioName(scenarioIds[0])}`;
    return `${resultOutcomeLabel(detail.outcomeClass)}${detail.outcomeLabels?.sar_filed === true ? ' · SAR filed' : ''}${claimedLine}. `
        + 'Re-investigated with the selected agents and compared with the historical record.';
};

/**
 * One replayed case across everything the run compared: the stage's decisions,
 * the path the investigation took, each selected agent's verdict with its
 * factors beneath it, and the typologies either side named.
 *
 * A side that recorded nothing says why ("no data to read", "not run") rather
 * than showing a blank — an agent with nothing to read has not cleared the case.
 */
const CaseSections: React.FC<{ detail: CaseDetailResponse; manifest: Map<string, ComparisonDimension>; scenarios: RunScenarios }> = ({
    detail, manifest, scenarios
}) => {
    const comparisons = detail.comparisons ?? [];
    const of = (type: string) => comparisons.filter((c) => c.dimensionType === type);
    const factors = of('factor');
    // Only the typologies someone named: the rest are "neither named", which is most of them.
    const typologies = of('typology').filter((c) => c.agreement !== 'both_clear' && c.agreement !== 'not_comparable');
    const route = Array.isArray((detail.componentStatus as any)?.route)
        ? ((detail.componentStatus as any).route as RouteStep[]) : [];
    const typologyOf = (id: string) => {
        if (!scenarios) return '';
        const labels = scenarios[id]?.typologies?.map((t) => t.label) ?? [];
        return labels.length ? ` → ${labels.join(', ')}` : ' (not mapped)';
    };

    return (
        <>
            <Section title="Decisions">
                {of('decision').map((c) => (
                    <ComparisonRow key={c.dimensionKey} comparison={c} dimension={dimensionFor(manifest, c)} />
                ))}
            </Section>

            {route.length > 0 && (
                <Section title="Investigation path">
                    {route.map((step, i) => (
                        <Box key={`${step.node}-${i}`} className="flex items-center justify-between gap-2" sx={{ py: 0.5 }}>
                            {/* The template's name for the reader; its code, for matching the step to the flow, on hover. */}
                            <Tooltip arrow describeChild placement="top-start" title={`Activity template: ${step.node}`}>
                                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-body'), cursor: 'help' }}>
                                    {routeStepName(step)}
                                </Typography>
                            </Tooltip>
                            {step.mode && <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>{step.mode}</Typography>}
                        </Box>
                    ))}
                </Section>
            )}

            <Section title="Agents and their red flags">
                {of('agent').map((agent) => (
                    <Box key={agent.dimensionKey}>
                        <ComparisonRow comparison={agent} dimension={dimensionFor(manifest, agent)} />
                        {factors.filter((f) => f.producer === agent.producer).map((f) => (
                            <ComparisonRow key={f.dimensionKey} comparison={f} dimension={dimensionFor(manifest, f)} indent />
                        ))}
                    </Box>
                ))}
            </Section>

            <Section title="Typologies named by either side" always>
                {typologies.length ? typologies.map((c) => (
                    <ComparisonRow key={c.dimensionKey} comparison={c} dimension={dimensionFor(manifest, c)} />
                )) : (
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>Neither side named a typology for this case.</Typography>
                )}
            </Section>

            {detail.alerts.length > 0 && (
                <Section title={`Alerts in this case (${detail.alerts.length})`}>
                    {detail.alerts.map((alert) => (
                        <Box key={alert.alertRef} sx={{ py: 0.75, borderBottom: `1px solid ${dsVar('line')}` }}>
                            <Code>{alert.alertRef}</Code>
                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.5 }}>
                                {[
                                    alert.scenarioId ? `${scenarioName(alert.scenarioId)}${typologyOf(alert.scenarioId)}` : null,
                                    alert.institutionTierRaw ? `your tier ${alert.institutionTierRaw}` : null,
                                    resultOutcomeLabel(alert.disposition),
                                    alert.sarFiled ? 'SAR filed' : null
                                ].filter(Boolean).join(' · ')}
                            </Typography>
                        </Box>
                    ))}
                </Section>
            )}
        </>
    );
};

/** Back from a case to the item it was opened from. Styled with sx: Tailwind's utilities apply only under #root. */
const BackToItem: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
    <Box sx={{ mb: 3 }}>
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                maxWidth: '100%', display: 'inline-flex', alignItems: 'center', gap: 0.75, py: 0.75, px: 1.25,
                borderRadius: '8px', border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page'), color: dsVar('fg-2'),
                fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                lineHeight: 1.4, cursor: 'pointer', transition: 'color 150ms, border-color 150ms',
                '&:hover': { color: dsVar('accent-fg'), borderColor: 'rgba(255, 222, 89, 0.4)' }
            }}
        >
            <ArrowBackIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Back to {label}
            </Box>
        </Box>
    </Box>
);

/**
 * The investigation run's one drawer, docked beside the tabs like the rest of the
 * dashboard's: a compared item a scorecard row opened (its figures, grid and
 * cases), or a replayed case.
 *
 * A case opened from an item's cases takes the drawer's place with a way back,
 * rather than stacking a second drawer. The item stays mounted underneath, so
 * going back finds its cell, filter, page and scroll position as they were.
 */
const InvestigationDrawer: React.FC<InvestigationDrawerProps> = ({
    runKey, dimension, caseRef, manifest, scenarios = null, withheld, outcomes, onCaseSelect, onBack, onClose,
    expanded, onToggleExpand
}) => {
    const [detail, setDetail] = useState<CaseDetailResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!runKey || !caseRef) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setDetail(null);
        benchmarkService.getCaseDetail(runKey, caseRef)
            .then((data) => { if (!cancelled) setDetail(data); })
            .catch((err) => {
                if (!cancelled) setError(err instanceof BenchmarkRequestError ? err.message : 'Could not load this case.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [runKey, caseRef, attempt]);

    // The drawer's body is the scroll container; an anchor inside it reaches it.
    const anchorRef = useRef<HTMLDivElement>(null);
    const itemScroll = useRef(0);
    const showingCase = Boolean(caseRef);
    const scroller = () => anchorRef.current?.parentElement ?? null;
    useLayoutEffect(() => {
        const el = scroller();
        if (!el) return;
        if (showingCase) {
            if (dimension) itemScroll.current = el.scrollTop;
            el.scrollTop = 0;
        } else {
            el.scrollTop = itemScroll.current;
        }
    }, [showingCase]); // eslint-disable-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        itemScroll.current = 0;
        const el = scroller();
        if (el) el.scrollTop = 0;
    }, [dimension?.dimensionKey]);

    const open = Boolean(runKey && (dimension || caseRef));

    return (
        <EntityDetailDrawer
            open={open}
            onClose={onClose}
            docked
            dockedMinWidth={1280}
            width={560}
            expanded={expanded}
            onToggleExpand={onToggleExpand}
            icon={showingCase ? <ManageSearchIcon /> : <CompareArrowsIcon />}
            subtitle={showingCase ? 'Replayed case' : dimension ? DIMENSION_TYPE_LABELS[dimension.dimensionType] ?? dimension.dimensionType : undefined}
            title={(showingCase ? caseRef : dimension?.label) ?? ''}
            description={showingCase
                ? (detail ? caseDescription(detail, scenarios) : '')
                : dimension ? dimensionSentence(dimension) : ''}
            banner={showingCase ? (
                dimension ? <BackToItem label={dimension.label} onClick={onBack} /> : undefined
            ) : dimension ? (
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mb: 1 }}>
                        Produced by {dimension.producerLabel ?? producerLabel(dimension.producer)}
                        {dimension.drivesDecision === false ? ' · compared only: it cannot move this review\'s decisions' : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <DimensionBadges summary={dimension} withheld={withheld} />
                    </Box>
                </Box>
            ) : undefined}
        >
            <Box ref={anchorRef} sx={{ display: 'none' }} />
            {dimension && runKey ? (
                <Box sx={{ display: showingCase ? 'none' : 'block', mt: 3 }}>
                    <DimensionDetailBody
                        runKey={runKey}
                        summary={dimension}
                        onCaseSelect={onCaseSelect}
                        selectedCaseRef={caseRef}
                        outcomes={outcomes}
                    />
                </Box>
            ) : null}

            {showingCase && (
                <>
                    {loading && (
                        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} /></Box>
                    )}
                    {error && <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />}
                    {detail && <CaseSections detail={detail} manifest={manifest} scenarios={scenarios} />}
                </>
            )}
        </EntityDetailDrawer>
    );
};

export default InvestigationDrawer;
